import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useSubscription } from '../hooks/useSubscription';
import { Button } from '../components/ui/Button';
import { Plus, FileText, Clock, Star, Folder, Loader2, AlertCircle, Crown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import SubscriptionStatus from '../components/SubscriptionStatus';
import { DocumentLimitWarning } from '../components/DocumentLimitWarning';
import { AnalyticsDashboard } from '../components/AnalyticsDashboard';
import { APIKeyManagement } from '../components/APIKeyManagement';
import { extractTemplateContent } from '../utils/templateUtils';
import { useTranslation } from '../lib/i18n';

interface Document {
  id: string;
  title: string;
  updated_at: string;
}

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  content: { text: string };
}

const Dashboard = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { subscription, canCreateDocument, hasFeature, refresh: refreshSubscription } = useSubscription();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [canCreate, setCanCreate] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'analytics' | 'api'>('overview');
  const [creatingDocument, setCreatingDocument] = useState(false);
  const [userFullName, setUserFullName] = useState<string | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  useEffect(() => {
    if (user) {
      // Force refresh subscription on mount
      refreshSubscription();
      loadDocuments();
      loadTemplates();
      loadUserProfile();
    }
  }, [user]);

  // Check document limit when subscription changes
  useEffect(() => {
    if (user && subscription) {
      checkDocumentLimit();
    }
  }, [user, subscription]);

  const checkDocumentLimit = async () => {
    const canCreateDoc = await canCreateDocument();
    setCanCreate(canCreateDoc);
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: docError } = await supabase
        .from('documents')
        .select('id, title, updated_at')
        .eq('user_id', user?.id)
        .order('updated_at', { ascending: false })
        .limit(5);

      if (docError) throw docError;
      setDocuments(data || []);
    } catch (error: any) {
      console.error('Error loading documents:', error);
      setError(t('dashboard.failedLoadDocuments'));
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      setTemplateError(null);

      const { data, error: templateError } = await supabase
        .from('templates')
        .select('id, name, description, category, content')
        .limit(3);

      if (templateError) throw templateError;
      setTemplates(data || []);
    } catch (error: any) {
      console.error('Error loading templates:', error);
      setTemplateError(t('dashboard.failedLoadTemplates'));
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadUserProfile = async () => {
    if (!user?.id) return;

    try {
      setLoadingProfile(true);

      const { data, error } = await supabase
        .from('users')
        .select('full_name')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error loading user profile:', error);
      } else if (data) {
        setUserFullName(data.full_name || null);
      }
    } catch (error: any) {
      console.error('Error loading user profile:', error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const createDocumentFromTemplate = async (template: Template) => {
    try {
      setCreatingDocument(true);
      setError(null);

      console.log('=== POPULAR TEMPLATE CLICKED ===');
      console.log('Template object:', template);
      console.log('Template content:', template.content);
      console.log('Content type:', typeof template.content);
      
      // Use centralized content extraction
      const templateContent = extractTemplateContent(template.content);
      
      console.log('Final extracted content length:', templateContent.length);
      console.log('Content preview:', templateContent.substring(0, 100));
      
      if (templateContent !== undefined) {
        navigate('/editor', {
          state: {
            templateContent,
            templateTitle: template.name
          }
        });
        console.log('✅ Popular template loaded successfully');
      } else {
        console.error('❌ Popular: No content found in template');
        setError('Template content could not be loaded');
      }
    } catch (error: any) {
      console.error('❌ Error loading template:', error);
      setError(error.message || 'Failed to load template');
    } finally {
      setCreatingDocument(false);
    }
  };

  if (!user) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-bold mb-4">{t('auth.welcomeTo')}</h2>
        <p className="mb-8">{t('auth.signInToAccess')}</p>
        <Link to="/auth">
          <Button>{t('common.signIn')}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">
            {loadingProfile ? (
              t('dashboard.welcomeBack')
            ) : userFullName ? (
              <>{t('dashboard.welcomeBackName', { name: userFullName })}</>
            ) : (
              <>{t('dashboard.welcomeBackName', { name: user.email?.split('@')[0] || '' })}</>
            )}
          </h1>
          {subscription && (
            <p className="text-gray-700 dark:text-gray-200 mt-1 font-semibold">
              {t('dashboard.currentPlan')} <span className="font-bold capitalize">{subscription.plan}</span>
              {subscription.plan !== 'free' && (
                <Crown className="w-4 h-4 inline ml-1 text-purple-500" />
              )}
            </p>
          )}
        </div>
        <div className="flex items-center space-x-3">
          {canCreate ? (
            <Link to="/editor">
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                {t('dashboard.newDocument')}
              </Button>
            </Link>
          ) : (
            <Button disabled title={t('dashboard.documentLimitReached')}>
              <Plus className="w-4 h-4 mr-2" />
              {t('dashboard.newDocument')}
            </Button>
          )}
        </div>
      </div>

      {/* Document Limit Warning */}
      <DocumentLimitWarning />

      {/* Subscription Status */}
      <SubscriptionStatus />

      {error && (
        <div className="flex items-center gap-2 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-200 p-4 rounded-lg">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Navigation Tabs for Premium Features */}
      {(hasFeature('analytics') || hasFeature('apiAccess')) && (
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('dashboard.overview')}
            </button>
            {hasFeature('analytics') && (
              <button
                onClick={() => setActiveTab('analytics')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'analytics'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {t('dashboard.analytics')}
              </button>
            )}
            {hasFeature('apiAccess') && (
              <button
                onClick={() => setActiveTab('api')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'api'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {t('dashboard.apiAccess')}
              </button>
            )}
          </nav>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">{t('dashboard.recentDocuments')}</h2>
                <Folder className="w-5 h-5 text-blue-500" />
              </div>
              <div className="space-y-3">
                {loading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                  </div>
                ) : documents.length > 0 ? (
                  documents.map((doc) => (
                    <Link
                      key={doc.id}
                      to={`/editor/${doc.id}`}
                      className="block p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {doc.title}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                            {t('dashboard.lastUpdated', { date: new Date(doc.updated_at).toLocaleDateString() })}
                          </p>
                        </div>
                        <FileText className="w-4 h-4 text-gray-400" />
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="text-gray-600 dark:text-gray-300 font-medium">{t('dashboard.noRecentDocuments')}</p>
                )}
                {documents.length > 0 && (
                  <Link
                    to="/documents"
                    className="block mt-4 text-blue-500 hover:text-blue-600 text-sm font-medium"
                  >
                    {t('dashboard.viewAllDocuments')}
                  </Link>
                )}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">{t('dashboard.popularTemplates')}</h2>
                <Star className="w-5 h-5 text-yellow-500" />
              </div>
              <div className="space-y-3">
                {loadingTemplates ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                  </div>
                ) : templateError ? (
                  <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                    <AlertCircle className="w-5 h-5" />
                    <p className="text-sm">{templateError}</p>
                  </div>
                ) : templates.length > 0 ? (
                  templates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => createDocumentFromTemplate(template)}
                      className="block p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {template.name}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-300 capitalize font-medium">
                            {template.category}
                          </p>
                        </div>
                        <FileText className="w-4 h-4 text-gray-400" />
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="text-gray-600 dark:text-gray-300 font-medium">{t('dashboard.noTemplatesAvailable')}</p>
                )}
                {templates.length > 0 && (
                  <Link
                    to="/templates"
                    className="block mt-4 text-blue-500 hover:text-blue-600 text-sm font-medium"
                  >
                    {t('dashboard.viewAllTemplates')}
                  </Link>
                )}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">{t('dashboard.aiAssistant')}</h2>
                <FileText className="w-5 h-5 text-purple-500" />
              </div>
              <p className="text-gray-700 dark:text-gray-200 mb-4 font-medium">
                {t('dashboard.aiAssistantDesc')}
              </p>
              <div className="space-y-2">
                <p className="text-sm text-gray-600 dark:text-gray-300 font-semibold">
                  {t('dashboard.aiLevel')} <span className="font-medium capitalize">{subscription?.features.aiProcessing}</span>
                </p>
                <Link to="/editor">
                  <Button variant="outline" className="w-full">
                    {t('dashboard.startNewProject')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">{t('dashboard.recentActivity')}</h2>
            <div className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                </div>
              ) : documents.length > 0 ? (
                <div className="space-y-4">
                  {documents.slice(0, 3).map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white">{doc.title}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                            {t('dashboard.updated', { date: new Date(doc.updated_at).toLocaleDateString() })}
                          </p>
                        </div>
                      </div>
                      <Link to={`/editor/${doc.id}`}>
                        <Button variant="ghost" size="sm">
                          {t('common.open')}
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-300 font-medium">{t('dashboard.noRecentActivity')}</p>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'analytics' && <AnalyticsDashboard />}
      {activeTab === 'api' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
          <APIKeyManagement />
        </div>
      )}
    </div>
  );
};

export default Dashboard;